/**
 * TaskDescriptionField Component
 *
 * Handles task description with rich text:
 * - Rich text formatting toolbar (bold, italic, lists, code)
 * - File/Image attachments display
 * - TextArea with markdown support
 * - File upload buttons
 *
 * `attachmentsOnly` renders JUST the attachment UI (drop/paste area, tags, attach
 * buttons) with no description textarea — used by request types whose own fields
 * replace the description (registry `hideDescription`, e.g. the IO Request Note).
 *
 * Extracted from TaskForm.js for maintainability.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Mentions, Button, Tag, Tooltip } from 'antd';
import {
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import FileDropPasteArea from '../../utils/FileDropPasteArea';
import FieldCharCount from '../shared/FieldCharCount';
import {
  allowedFileExtensions,
  allowedImageExtensions,
  DESCRIPTION_MAX_LENGTH,
} from '../../configs/task.config';
import '../../styles/index.scss';

const TaskDescriptionField = ({
  form,
  currentTaskId,
  textareaRef,
  fileInputRef,
  imageInputRef,
  attachedFiles,
  attachedImages,
  setAttachedFiles,
  setAttachedImages,
  setNewAttachedFiles,
  setNewAttachedImages,
  handleFileDelete,
  handleInsertCode,
  handleKeyDown,
  insertText,
  attachmentsOnly,
  mentionUsers,
}) => {
  const [mentionSearch, setMentionSearch] = useState('');

  // Options for the @ dropdown, filtered by what the user is typing.
  const renderMentionOptions = () =>
    (mentionUsers || [])
      .filter((u) => {
        if (!u.username) return false;
        if (!mentionSearch) return true;
        const q = mentionSearch.toLowerCase();
        return (
          (u.name && u.name.toLowerCase().includes(q)) || u.username.toLowerCase().includes(q)
        );
      })
      .slice(0, 20)
      .map((u) => (
        <Mentions.Option key={u.id} value={u.username}>
          {u.name ? `${u.name} (@${u.username})` : `@${u.username}`}
        </Mentions.Option>
      ));
  // Edit mode = a real persisted task. The modal/route create flows use the 'new' sentinel
  // (truthy), so a plain `if (currentTaskId)` check would wrongly treat create as edit. Use the
  // explicit sentinel-aware check everywhere.
  const isExistingTask = Boolean(currentTaskId && currentTaskId !== 'new');

  // Shared sinks for every attach path (drop, paste, hidden inputs). FileDropPasteArea's
  // API is onFilesAdded/onImagesAdded — the previous onFileDrop prop didn't exist, so
  // drag-drop/paste silently did nothing.
  const handleFilesAdded = (files) => {
    if (!files || files.length === 0) return;
    setAttachedFiles((prev) => [...prev, ...files]);
    if (isExistingTask) {
      setNewAttachedFiles((prev) => [...prev, ...files]);
    }
  };

  const handleImagesAdded = (images) => {
    if (!images || images.length === 0) return;
    setAttachedImages((prev) => [...prev, ...images]);
    if (isExistingTask) {
      setNewAttachedImages((prev) => [...prev, ...images]);
    }
  };

  const attachmentsDisplay = ((attachedFiles && attachedFiles.length > 0) ||
    (attachedImages && attachedImages.length > 0)) && (
    <div className="task-description-field__attachments">
      {attachedFiles && attachedFiles.length > 0 && (
        <Row className="task-description-field__attachment-row">
          {attachedFiles.map((file, index) => (
            <Tag
              closable
              key={index}
              onClose={() => {
                if (isExistingTask) {
                  handleFileDelete(file, 'file');
                } else {
                  const updatedFiles = attachedFiles.filter((_, i) => i !== index);
                  setAttachedFiles(updatedFiles);
                }
              }}
              icon={<PaperClipOutlined />}
              className="task-description-field__attachment-tag"
            >
              {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
            </Tag>
          ))}
        </Row>
      )}
      {attachedImages && attachedImages.length > 0 && (
        <Row className="task-description-field__attachment-row">
          {attachedImages.map((file, index) => (
            <Tag
              closable
              key={index}
              onClose={() => {
                if (isExistingTask) {
                  handleFileDelete(file, 'image');
                } else {
                  const updatedImages = attachedImages.filter((_, i) => i !== index);
                  setAttachedImages(updatedImages);
                }
              }}
              icon={<PictureOutlined />}
              className="task-description-field__attachment-tag"
            >
              {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
            </Tag>
          ))}
        </Row>
      )}
    </div>
  );

  const hiddenInputs = (
    <>
      <input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        accept={allowedFileExtensions.join(', ')}
        onChange={(file) => handleFilesAdded(Array.from(file.target.files))}
      />
      <input
        type="file"
        ref={imageInputRef}
        hidden
        multiple
        accept={allowedImageExtensions.join(', ')}
        onChange={(image) =>
          handleImagesAdded(
            Array.from(image.target.files).filter((f) => f.type.startsWith('image/'))
          )
        }
      />
    </>
  );

  if (attachmentsOnly) {
    return (
      <Row>
        <Col span={24}>
          <Form.Item
            label={
              <div>
                <div>Attachments</div>
                <div className="task-description-field__label-hint">
                  Drag &amp; drop or paste files/images, or use the buttons below
                </div>
              </div>
            }
            colon={false}
          >
            <FileDropPasteArea onFilesAdded={handleFilesAdded} onImagesAdded={handleImagesAdded}>
              <div className="task-attachments-only__drop-area">
                {attachmentsDisplay || (
                  <div className="task-attachments-only__hint">No attachments yet</div>
                )}
              </div>
              {hiddenInputs}
            </FileDropPasteArea>
            <Row className="task-description-field__action-row">
              <Tooltip title="Attach file" mouseEnterDelay={0.5}>
                <Button
                  onClick={() => fileInputRef.current.click()}
                  className="task-description-field__action-btn"
                >
                  <PaperClipOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Attach image" mouseEnterDelay={0.5}>
                <Button
                  onClick={() => imageInputRef.current.click()}
                  className="task-description-field__action-btn"
                >
                  <PictureOutlined />
                </Button>
              </Tooltip>
            </Row>
          </Form.Item>
        </Col>
      </Row>
    );
  }

  return (
    <Col className="task-description-field">
      <Card
        className="task-description-field__card"
        title={
          <Text className="task-description-field__card-title">TASK DESCRIPTION</Text>
        }
      >
        <Row>
          <Col span={24}>
            <Form.Item
              label={
                <div>
                  <div>Description</div>
                  <div className="task-description-field__label-hint">
                    Explain what the task is about and add any context that helps. Having
                    trouble or help needed? Discuss it here.
                  </div>
                </div>
              }
              colon={false}
            >
              <div className="task-description-field__editor-wrapper">
                  <FileDropPasteArea
                    onFilesAdded={handleFilesAdded}
                    onImagesAdded={handleImagesAdded}
                  >
                    {/* Formatting Toolbar */}
                    <div className="task-description-field__toolbar">
                      <Tooltip title="Bold" mouseEnterDelay={0.5}>
                        <Button
                          icon={<BoldOutlined />}
                          onClick={() => insertText({ before: '**', after: '**' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Italic" mouseEnterDelay={0.5}>
                        <Button
                          icon={<ItalicOutlined />}
                          onClick={() => insertText({ before: '*', after: '*' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                        <Button
                          icon={<StrikethroughOutlined />}
                          onClick={() => insertText({ before: '~~', after: '~~' })}
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                        <Button
                          icon={<OrderedListOutlined />}
                          onClick={() =>
                            insertText({
                              transform: (selectedText, start) => {
                                if (selectedText === '') {
                                  return { text: '1. ', newStart: start + 3, newEnd: start + 3 };
                                }
                                const lines = selectedText
                                  .split('\n')
                                  .filter((line) => line.trim() !== '');
                                const numbered = lines
                                  .map((line, i) => `${i + 1}. ${line}`)
                                  .join('\n');
                                return { text: numbered, newStart: start, newEnd: start + numbered.length };
                              },
                            })
                          }
                          type="text"
                        />
                      </Tooltip>
                      <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                        <Button
                          icon={<UnorderedListOutlined />}
                          onClick={() =>
                            insertText({
                              transform: (selectedText, start) => {
                                if (selectedText === '') {
                                  return { text: '- ', newStart: start + 2, newEnd: start + 2 };
                                }
                                const lines = selectedText
                                  .split('\n')
                                  .filter((line) => line.trim() !== '');
                                const dashed = lines.map((line) => `- ${line}`).join('\n');
                                return { text: dashed, newStart: start, newEnd: start + dashed.length };
                              },
                            })
                          }
                          type="text"
                        />
                      </Tooltip>
                    </div>

                    {/* Attachments Display */}
                    {attachmentsDisplay}

                    {/* TextArea */}
                    <div className="task-description-field__textarea-wrapper">
                      <Form.Item name="taskDetails" noStyle>
                        <Mentions
                          ref={textareaRef}
                          onChange={(text) => {
                            form.setFieldsValue({ taskDetails: text.slice(0, DESCRIPTION_MAX_LENGTH) });
                          }}
                          onSearch={(text) => setMentionSearch(text)}
                          filterOption={false}
                          onKeyDown={handleKeyDown}
                          autoSize={{ minRows: 5, maxRows: 10 }}
                          maxLength={DESCRIPTION_MAX_LENGTH}
                          placeholder="Enter task details"
                          className={`task-description-field__textarea custom-textarea ${
                            attachedFiles?.length || attachedImages?.length
                              ? 'task-description-field__textarea--with-attachments'
                              : 'task-description-field__textarea--no-attachments'
                          }`}
                        >
                          {renderMentionOptions()}
                        </Mentions>
                      </Form.Item>
                    </div>

                    {/* Hidden file inputs */}
                    {hiddenInputs}
                  </FileDropPasteArea>

                {/* Attachment Buttons */}
                <Row className="task-description-field__action-row">
                  <Tooltip title="Attach file" mouseEnterDelay={0.5}>
                    <Button
                      onClick={() => fileInputRef.current.click()}
                      className="task-description-field__action-btn"
                    >
                      <PaperClipOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Attach image" mouseEnterDelay={0.5}>
                    <Button
                      onClick={() => imageInputRef.current.click()}
                      className="task-description-field__action-btn"
                    >
                      <PictureOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Code block" mouseEnterDelay={0.5}>
                    <Button
                      onClick={handleInsertCode}
                      className="task-description-field__action-btn"
                    >
                      <CodeOutlined />
                    </Button>
                  </Tooltip>

                  <FieldCharCount name="taskDetails" max={DESCRIPTION_MAX_LENGTH} />
                </Row>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

TaskDescriptionField.propTypes = {
  form: PropTypes.object.isRequired,
  currentTaskId: PropTypes.string,
  textareaRef: PropTypes.object.isRequired,
  fileInputRef: PropTypes.object.isRequired,
  imageInputRef: PropTypes.object.isRequired,
  attachedFiles: PropTypes.array.isRequired,
  attachedImages: PropTypes.array.isRequired,
  setAttachedFiles: PropTypes.func.isRequired,
  setAttachedImages: PropTypes.func.isRequired,
  setNewAttachedFiles: PropTypes.func.isRequired,
  setNewAttachedImages: PropTypes.func.isRequired,
  handleFileDelete: PropTypes.func.isRequired,
  handleInsertCode: PropTypes.func.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  insertText: PropTypes.func.isRequired,
  attachmentsOnly: PropTypes.bool,
  mentionUsers: PropTypes.array,
};

TaskDescriptionField.defaultProps = {
  currentTaskId: null,
  attachmentsOnly: false,
  mentionUsers: [],
};

export default TaskDescriptionField;
